/**
 * emotionalCapacityEvaluator.js
 *
 * Evaluates the PERSON'S emotional bandwidth — how much they can
 * hold, process, and engage with at this moment.
 *
 * DISTINCT FROM empathyBalanceEngine (FASE 6):
 *   empathyBalanceEngine → Kevia's response calibration (over_empathy vs over_clarity)
 *   THIS                 → the PERSON'S internal capacity (overwhelmed vs receptive)
 *
 * Capacity levels:
 *   overwhelmed  — at emotional ceiling; any load increase causes harm
 *   depleted     — very low; has been holding too much for too long
 *   strained     — under meaningful pressure; reduced bandwidth
 *   stable       — baseline; normal emotional processing available
 *   receptive    — calm and open; high capacity for engagement
 */

/**
 * @param {{
 *   emotion:            object|null,
 *   processState:       string,
 *   regressionState:    string,
 *   conversationLength: number,
 *   ecclesiaLoad:       string,
 *   silentTension:      number,
 * }} params
 * @returns {{
 *   capacity:    string,
 *   instruction: string|null,
 * }}
 */
export function evaluateEmotionalCapacity({
  emotion            = null,
  processState       = "apertura",
  regressionState    = "stable",
  conversationLength = 0,
  ecclesiaLoad       = "low",
  silentTension      = 0,
}) {
  const intensity = emotion?.intensity ?? 0;
  const dominant  = emotion?.dominant ?? "neutral";

  let score = 0; // Higher score = more strained

  // Process state contribution
  if (processState === "agotamiento_espiritual") score += 4;
  else if (processState === "quebranto")         score += 3;
  else if (processState === "resistencia")       score += 2;
  else if (processState === "confusión")         score += 1;

  // Emotion intensity contribution
  if (intensity >= 0.85) score += 3;
  else if (intensity >= 0.7) score += 2;
  else if (intensity >= 0.5) score += 1;

  // Dominant emotion (grief and anxiety are especially draining)
  if (dominant === "grief" || dominant === "anxiety" || dominant === "despair") score += 1;

  // Regression state
  if (regressionState === "sustained_regression") score += 2;
  else if (regressionState === "regression")      score += 1;

  // Long conversation = cumulative fatigue
  if (conversationLength > 8)  score += 2;
  else if (conversationLength > 5) score += 1;

  // Pastoral load (from ecclesia layer)
  if (ecclesiaLoad === "high")   score += 2;
  else if (ecclesiaLoad === "medium") score += 1;

  // Silent tension accumulation
  score += Math.min(silentTension, 2);

  // Map score to capacity level
  if (score >= 8) {
    return {
      capacity: "overwhelmed",
      instruction: "Capacidad emocional al límite. Menos es más — simplifica, acorta, no exijas.",
    };
  }
  if (score >= 5) {
    return {
      capacity: "depleted",
      instruction: "Capacidad emocional muy baja. Respuestas breves y presencia gentil.",
    };
  }
  if (score >= 3) {
    return {
      capacity: "strained",
      instruction: null,
    };
  }
  if (score <= 0 && intensity < 0.25 && processState === "apertura") {
    return {
      capacity: "receptive",
      instruction: null,
    };
  }

  return { capacity: "stable", instruction: null };
}
